import { EmptyImageComponents } from 'app/components/empty-graphic.constants';
import type { ButtonProps } from 'app/design/primitives/button';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { useUISize } from 'app/utils/web-helpers';
import type { PropsWithChildren } from 'react';
import { type ImageRequireSource, View } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

interface EmptyGraphicProps {
  primary?: string;
  secondary?: string;
  button?: ButtonProps;
  invertTexts?: boolean;
  // Source can be either an image source (URI)
  source?: string | ImageRequireSource | undefined;
  // Or we'll use the specified image (defaults to Shed)
  image?: keyof typeof EmptyImageComponents;
  secondaryTextAlign?: 'center' | 'left';
}

export function EmptyGraphic({
  button,
  primary,
  secondary,
  source,
  image = 'Shed',
  invertTexts,
  secondaryTextAlign = 'center',
}: PropsWithChildren<EmptyGraphicProps>) {
  const { isLargeUI } = useUISize();
  const ImageComponent = EmptyImageComponents[image];
  return (
    <View style={{ marginHorizontal: theme.spacing['spacing.general.x8'] }}>
      {source ? (
        <PCImage
          alignSelf="center"
          contentFit="contain"
          height={250}
          source={typeof source === 'string' ? { uri: source } : source}
          style={{ width: '100%' }}
        />
      ) : (
        <View style={{ alignSelf: 'center', paddingVertical: theme.spacing['spacing.general.x8'] }}>
          <ImageComponent />
        </View>
      )}

      {primary && !invertTexts ? (
        <Text marginTop="spacing.general.x8" textAlign="center" variant="bodyBase">
          {primary}
        </Text>
      ) : null}

      {secondary ? (
        <Text
          fontWeight="700"
          marginTop="spacing.general.x8"
          textAlign={invertTexts ? 'center' : secondaryTextAlign}
          variant="bodyBase"
        >
          {secondary}
        </Text>
      ) : null}

      {primary && invertTexts ? (
        <Text marginTop="spacing.general.x4" textAlign={secondaryTextAlign} variant="bodyBase">
          {primary}
        </Text>
      ) : null}

      {button ? (
        <Button
          alignSelf={isLargeUI ? 'center' : undefined}
          marginBottom="spacing.general.x8"
          marginTop="spacing.general.x8"
          variant="primary"
          {...button}
        />
      ) : null}
    </View>
  );
}
